import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Check, ChevronRight, Shield, Loader2, X, Flame, Zap, Globe, Server, Headphones, RefreshCw, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const oneTimePlans = [
  {
    name: "Starter",
    slug: "starter",
    price: "79",
    originalPrice: "129",
    description: "Personnalisation basique, prêt à publier",
    features: [
      "Vos couleurs et typographies appliquées",
      "Vos textes et photos intégrés",
      "Dark & Light mode configuré",
      "Déploiement inclus",
      "1 session de révision",
      "Livraison sous 72h",
    ],
    notIncluded: ["Bilingue FR/EN", "SEO avancé", "Sections custom"],
    cta: "Choisir Starter",
    popular: false,
    icon: null,
  },
  {
    name: "Clé en main",
    slug: "cle_en_main",
    price: "149",
    originalPrice: "249",
    description: "Le plus demandé — portfolio complet et optimisé",
    features: [
      "Tout du Starter",
      "Bilingue FR/EN configuré",
      "SEO complet pour être visible sur Google",
      "Sections activées selon votre profil",
      "2 sessions de révision",
      "Livraison sous 48h",
    ],
    notIncluded: ["Sections custom", "Support prioritaire"],
    cta: "Commander mon portfolio",
    popular: true,
    icon: Flame,
  },
  {
    name: "Sur-mesure",
    slug: "sur_mesure",
    price: "349",
    originalPrice: "549",
    description: "Portfolio unique avec fonctionnalités custom",
    features: [
      "Tout du Clé en main",
      "Sections développées sur demande",
      "Animations et interactions custom",
      "5 sessions de révision",
      "Support prioritaire 60 jours",
    ],
    notIncluded: [],
    cta: "Demander un devis",
    popular: false,
    icon: Zap,
  },
];

const subscriptionPlans = [
  {
    name: "Domaine Essentiel",
    slug: "domaine_essentiel",
    price: "9.90",
    description: "Votre nom de domaine professionnel",
    features: [
      "Nom de domaine personnalisé (.com, .fr…)",
      "Hébergement haute performance",
      "Connexion sécurisée (cadenas 🔒)",
      "Disponibilité garantie 99.9%",
      "Support par email",
    ],
    notIncluded: ["Maintenance mensuelle", "Modifications incluses", "Support prioritaire"],
    cta: "Souscrire",
    popular: false,
    icon: Globe,
  },
  {
    name: "Domaine Pro",
    slug: "domaine_pro",
    price: "19.90",
    description: "Domaine + maintenance — Le plus choisi",
    features: [
      "Tout de l'Essentiel",
      "Maintenance & mises à jour mensuelles",
      "Sauvegarde automatique",
      "Monitoring 24/7",
      "1 modification mineure/mois",
      "Support prioritaire",
    ],
    notIncluded: ["Modifications illimitées"],
    cta: "Souscrire au Pro",
    popular: true,
    icon: Server,
  },
  {
    name: "Domaine Premium",
    slug: "domaine_premium",
    price: "34.90",
    description: "Service tout inclus — 0 tracas",
    features: [
      "Tout du Pro",
      "Modifications mensuelles illimitées",
      "Refonte design annuelle offerte",
      "Analytics & rapports mensuels",
      "Support prioritaire WhatsApp",
      "Interlocuteur dédié",
    ],
    notIncluded: [],
    cta: "Souscrire au Premium",
    popular: false,
    icon: Headphones,
  },
];

function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState({ h: 0, m: 0, s: 0 });
  const endRef = useRef<number>(0);

  useEffect(() => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    endRef.current = end.getTime();

    const tick = () => {
      const diff = Math.max(0, endRef.current - Date.now());
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ h, m, s });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex items-center gap-1 text-sm font-mono font-bold">
      <span className="px-2 py-1 rounded bg-destructive/10 text-destructive">{String(timeLeft.h).padStart(2, "0")}</span>
      <span className="text-destructive">:</span>
      <span className="px-2 py-1 rounded bg-destructive/10 text-destructive">{String(timeLeft.m).padStart(2, "0")}</span>
      <span className="text-destructive">:</span>
      <span className="px-2 py-1 rounded bg-destructive/10 text-destructive">{String(timeLeft.s).padStart(2, "0")}</span>
    </div>
  );
}

interface PlanCardProps {
  plan: typeof oneTimePlans[0] | typeof subscriptionPlans[0];
  index: number;
  isSubscription: boolean;
  loadingPlan: string | null;
  onCheckout: (slug: string) => void;
}

function PlanCard({ plan, index, isSubscription, loadingPlan, onCheckout }: PlanCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15 }}
      className={`relative p-6 sm:p-8 rounded-2xl border transition-all duration-300 ${
        plan.popular
          ? "border-primary bg-gradient-to-b from-primary/8 to-transparent shadow-xl shadow-primary/10 md:scale-105 md:-my-4"
          : "border-border/50 bg-card/50 hover:border-primary/30"
      }`}
    >
      {plan.popular && (
        <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 sm:px-5 py-1.5 rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground text-xs font-bold shadow-lg shadow-primary/30 whitespace-nowrap">
          ⚡ Le + populaire
        </span>
      )}
      <div className="flex items-center gap-2 mb-1">
        {plan.icon && <plan.icon className="w-5 h-5 text-primary flex-shrink-0" />}
        <h3 className="text-lg sm:text-xl font-bold truncate">{plan.name}</h3>
       </div>
      <p className="text-muted-foreground text-xs sm:text-sm mb-5">{plan.description}</p>
      <div className="mb-6 sm:mb-8">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-4xl sm:text-5xl font-black">{plan.price}</span>
          <span className="text-lg sm:text-xl font-bold">€</span>
          {isSubscription && <span className="text-xs sm:text-sm text-muted-foreground">/mois</span>}
          {"originalPrice" in plan && plan.originalPrice && (
            <span className="text-base sm:text-lg text-muted-foreground line-through ml-1">{plan.originalPrice}€</span>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="ml-1 text-muted-foreground hover:text-primary transition-colors">
                <HelpCircle className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-xs leading-relaxed">
              {isSubscription ? (
                <p>Comprend l'hébergement, le domaine et la maintenance. Sans engagement, résiliable à tout moment.</p>
              ) : plan.popular ? (
                <p>Formule la plus choisie. Un freelance facturerait 2 500€+ pour le même résultat. Inclut tout pour être en ligne en 48h.</p>
              ) : Number(plan.price) < 100 ? (
                <p>Idéal pour démarrer rapidement. Même qualité technique qu'un site à 2 000€, personnalisation basique.</p>
              ) : (
                <p>Pour les professionnels exigeants. Sections sur-mesure, animations custom et support prioritaire inclus.</p>
              )}
            </TooltipContent>
          </Tooltip>
        </div>
        <span className="text-[10px] sm:text-xs text-muted-foreground">
          {isSubscription ? (
            <>
              <RefreshCw className="w-3 h-3 inline mr-1" />
              Sans engagement
            </>
          ) : (
            "paiement unique"
          )}
        </span>
      </div>
      <ul className="space-y-2.5 sm:space-y-3 mb-5 sm:mb-6">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-xs sm:text-sm">
            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
            </div>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {plan.notIncluded.length > 0 && (
        <ul className="space-y-2 mb-6 sm:mb-8 pt-2 border-t border-border/30">
          {plan.notIncluded.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-xs sm:text-sm text-muted-foreground/60">
              <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      {plan.notIncluded.length === 0 && <div className="mb-6 sm:mb-8" />}
      <Button
        className={`w-full py-5 sm:py-6 text-sm sm:text-base font-bold ${
          plan.popular
            ? "bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/20"
            : ""
        }`}
        variant={plan.popular ? "default" : "outline"}
        size="lg"
        disabled={loadingPlan !== null}
        onClick={() => onCheckout(plan.slug)}
      >
        {loadingPlan === plan.slug ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <ChevronRight className="w-4 h-4 mr-2" />
        )}
        {loadingPlan === plan.slug ? "Redirection..." : plan.cta}
      </Button>
      {isSubscription && (
        <p className="text-[10px] sm:text-xs text-muted-foreground text-center mt-3">
          Résiliation possible à tout moment.
        </p>
      )}
    </motion.div>
  );
}

const SellPricing = () => {
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleCheckout = async (slug: string) => {
    setLoadingPlan(slug);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan: slug },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (err: any) {
      console.error("Checkout error:", err);
      toast.error("Erreur lors de la création du paiement. Réessayez.");
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section id="pricing" className="py-20 sm:py-24 relative overflow-hidden scroll-mt-20">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/3 to-transparent" />
      <div className="container mx-auto px-4 sm:px-6 relative">
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs sm:text-sm font-semibold mb-4">
            💰 Tarifs transparents
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 tracking-tight">
            Un investissement, pas une dépense
          </h2>
          <p className="text-muted-foreground text-sm sm:text-base lg:text-lg max-w-2xl mx-auto mb-6">
            Un freelance web facture 1 500–5 000€ pour un portfolio. Une agence, 3 000–10 000€.
            <br className="hidden sm:block" />
            <span className="text-foreground font-bold">Chez Folio Studio, même qualité — à partir de 79€.</span>
          </p>
          {/* Anchor pricing visual */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 text-xs sm:text-sm mb-2">
            <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl bg-destructive/5 border border-destructive/20 text-muted-foreground line-through">
              Freelance : ~2 500€
            </span>
            <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl bg-destructive/5 border border-destructive/20 text-muted-foreground line-through">
              Agence : ~5 000€
            </span>
            <span className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl bg-primary/10 border border-primary/30 text-primary font-bold">
              Folio Studio : dès 79€ ✨
            </span>
          </div>
        </motion.div>

        {/* Urgency banner */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10 p-3 sm:p-4 rounded-2xl bg-destructive/5 border border-destructive/20 max-w-lg mx-auto"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
        >
          <span className="text-xs sm:text-sm font-bold text-destructive">🔥 Offre de lancement — se termine dans</span>
          <CountdownTimer />
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="creation" className="max-w-5xl mx-auto">
          <TabsList className="grid w-full max-w-sm sm:max-w-md mx-auto grid-cols-2 mb-8 sm:mb-10 h-11 sm:h-12">
            <TabsTrigger value="creation" className="text-xs sm:text-sm font-bold">
              🎨 Création
            </TabsTrigger>
            <TabsTrigger value="domaine" className="text-xs sm:text-sm font-bold">
              🌐 Abonnement
            </TabsTrigger>
          </TabsList>

          <TabsContent value="creation">
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 items-start">
              {oneTimePlans.map((plan, i) => (
                <PlanCard
                  key={plan.slug}
                  plan={plan}
                  index={i}
                  isSubscription={false}
                  loadingPlan={loadingPlan}
                  onCheckout={handleCheckout}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="domaine">
            <motion.div
              className="mb-6 sm:mb-8 p-3 sm:p-4 rounded-xl bg-accent/5 border border-accent/20 max-w-2xl mx-auto text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <p className="text-xs sm:text-sm text-muted-foreground">
                <Globe className="w-4 h-4 inline mr-1 text-primary" />
                <strong className="text-foreground">Comment ça marche :</strong> Votre domaine reste actif tant que votre abonnement est en cours. 
                En cas de résiliation, le domaine reste fonctionnel jusqu'à la fin de la période payée.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6 items-start">
              {subscriptionPlans.map((plan, i) => (
                <PlanCard
                  key={plan.slug}
                  plan={plan}
                  index={i}
                  isSubscription={true}
                  loadingPlan={loadingPlan}
                  onCheckout={handleCheckout}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Guarantee + Payment */}
        <motion.div
          className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-3 px-5 sm:px-6 py-3 rounded-2xl bg-card/60 border border-border/50 backdrop-blur-sm">
            <Shield className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm">
              <strong>Satisfait ou remboursé</strong> — 14 jours
            </span>
          </div>
          <div className="inline-flex items-center gap-3 px-5 sm:px-6 py-3 rounded-2xl bg-card/60 border border-border/50 backdrop-blur-sm">
            <Shield className="w-5 h-5 text-enterprise-success flex-shrink-0" />
            <span className="text-xs sm:text-sm">
              <strong>Paiement sécurisé</strong> — via Stripe
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SellPricing;
